// ---------------------------------------------------------------------------
// useCollectedData — Merged collected_data with pre-edit snapshot fallback
// ---------------------------------------------------------------------------
// When a user navigates from summary to a previous step, the snapshot
// preserves all collected_data at that point. After re-submitting the edited
// step, the backend clears downstream fields. This hook merges the live
// collected_data with the snapshot so downstream steps can still pre-fill.
// ---------------------------------------------------------------------------

import { useMemo } from 'react';
import { useSessionState } from './useSession';

/**
 * Shallow-merge helper: snapshot as base, live data overrides.
 * For nested objects (one level deep — dso, retailer, address, authorities),
 * the merge recurses one level so individual sub-keys are preserved.
 */
function mergeData(
  snapshot: Record<string, unknown>,
  live: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...snapshot };

  for (const [key, val] of Object.entries(live)) {
    if (
      val !== null &&
      val !== undefined &&
      typeof val === 'object' &&
      !Array.isArray(val) &&
      result[key] !== null &&
      result[key] !== undefined &&
      typeof result[key] === 'object' &&
      !Array.isArray(result[key])
    ) {
      // Merge nested objects one level deep
      result[key] = {
        ...(result[key] as Record<string, unknown>),
        ...(val as Record<string, unknown>),
      };
    } else if (val !== undefined) {
      result[key] = val;
    }
  }

  return result;
}

/**
 * Returns collected_data merged with the pre-edit snapshot.
 * Live collected_data takes priority over snapshot values.
 * Falls back to snapshot only for keys that are missing from live data.
 */
export function useCollectedData(): Record<string, unknown> {
  const { session, preEditSnapshot } = useSessionState();

  return useMemo(() => {
    const live = session?.collected_data ?? {};
    if (!preEditSnapshot) return live;
    return mergeData(preEditSnapshot, live);
  }, [session?.collected_data, preEditSnapshot]);
}
